import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';

import * as actions from '../store/modules/auth/actions';
import OficinaLogin from './pages/OficinaLogin';
import PlacaVeiculo from './pages/PlacaVeiculo';
import FotosInspecao from './pages/FotosInspecao';
import { Aviso } from './components';
import { aplicarManifestOficina, solicitarArmazenamentoPersistente, useInstalacao, useOnline } from './pwa';

import './oficina.css';

/*
 * eloOficina Web — versão ReactJS do app React Native "elooficina".
 * Publicado em www.eloadmin.com.br/oficina, usa a mesma API e a mesma
 * sessão (redux auth) do eloAdmin, mas com layout próprio para celular.
 *
 * Fluxo: Login -> Placa do veículo -> Fotos da inspeção -> envio (/oficinaapp).
 */
const CHAVE_TELA = 'elooficina:tela';
const CHAVE_INSTALACAO_DISPENSADA = 'elooficina:instalacao-dispensada';

const lerLocal = (chave) => {
  try { return localStorage.getItem(chave); } catch (e) { return null; }
};
const gravarLocal = (chave, valor) => {
  try { localStorage.setItem(chave, valor); } catch (e) { /* indisponível */ }
};

const lerTelaSalva = () => {
  try {
    const salvo = JSON.parse(sessionStorage.getItem(CHAVE_TELA) || 'null');
    if (salvo && salvo.tela === 'fotos' && salvo.placa) return salvo;
  } catch (e) {
    // sessionStorage indisponível
  }
  return { tela: 'placa', placa: '' };
};

const tokenExpirado = (token) => {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    return exp ? exp * 1000 <= Date.now() : false;
  } catch (e) {
    return true;
  }
};

export default function OficinaApp() {
  const dispatch = useDispatch();
  const { isLoggedIn, token, user } = useSelector((state) => state.auth);
  const [navegacao, setNavegacao] = useState(lerTelaSalva);
  const [aviso, setAviso] = useState(null);
  const [instalacaoDispensada, setInstalacaoDispensada] = useState(() => Boolean(lerLocal(CHAVE_INSTALACAO_DISPENSADA)));
  const online = useOnline();
  const instalacao = useInstalacao();

  // Sem internet o token vencido é mantido: dá para fotografar e guardar no aparelho;
  // o login é pedido de novo quando a conexão voltar (ou no envio).
  const logado = isLoggedIn && (!tokenExpirado(token) || !online);

  useEffect(() => {
    document.title = 'eloOficina';
    document.documentElement.classList.add('oficina-html');
    aplicarManifestOficina();
    solicitarArmazenamentoPersistente();
    return () => document.documentElement.classList.remove('oficina-html');
  }, []);

  // Sessão salva com token vencido: limpa para pedir login novamente (quando online).
  useEffect(() => {
    if (online && isLoggedIn && tokenExpirado(token)) dispatch(actions.loginFailure());
  }, [online, isLoggedIn, token, dispatch]);

  const dispensarInstalacao = () => {
    gravarLocal(CHAVE_INSTALACAO_DISPENSADA, '1');
    setInstalacaoDispensada(true);
  };

  const mostrarInstalacao = !instalacaoDispensada && (instalacao.podeInstalar || instalacao.instrucaoIos);

  useEffect(() => {
    try {
      sessionStorage.setItem(CHAVE_TELA, JSON.stringify(navegacao));
    } catch (e) {
      // sessionStorage indisponível
    }
  }, [navegacao]);

  const abrirFotos = useCallback((placa) => setNavegacao({ tela: 'fotos', placa }), []);
  const novaInspecao = useCallback(() => setNavegacao({ tela: 'placa', placa: '' }), []);
  const sair = useCallback(() => dispatch(actions.loginFailure()), [dispatch]);

  const confirmarSaida = () => setAviso({
    titulo: 'Sair',
    mensagem: 'Deseja encerrar a sessão? As fotos ainda não enviadas continuam salvas neste aparelho.',
    textoConfirmar: 'Sair',
    onConfirmar: sair,
  });

  const fecharAviso = useCallback(() => {
    setAviso((atual) => {
      if (atual && atual.onFechar) setTimeout(atual.onFechar, 0);
      return null;
    });
  }, []);

  return (
    <div className="oficina-app">
      {logado ? (
        <>
          <header className="oficina-topo">
            <div className="oficina-topo-marca">elo<strong>Oficina</strong></div>
            <div className="oficina-topo-usuario">
              <span title={user.email}>{user.nome || user.email}</span>
              <button type="button" className="oficina-icone-botao" aria-label="Sair" onClick={confirmarSaida}>
                <i className="pi pi-sign-out" />
              </button>
            </div>
          </header>
          {!online ? (
            <div className="oficina-faixa oficina-faixa-offline" role="status">
              <i className="pi pi-wifi" /> Sem internet — as fotos ficam salvas no aparelho e podem ser enviadas depois.
            </div>
          ) : null}
          <main className="oficina-conteudo">
            {mostrarInstalacao ? (
              <div className="oficina-instalar">
                <img src={`${process.env.PUBLIC_URL}/icon-192.png`} alt="" />
                <div>
                  <strong>Instale o eloOficina</strong>
                  {instalacao.podeInstalar ? (
                    <span>Abra direto da tela inicial, em tela cheia e mesmo sem internet.</span>
                  ) : (
                    <span>No Safari, toque em <i className="pi pi-upload" aria-label="Compartilhar" /> e depois em <b>Adicionar à Tela de Início</b>.</span>
                  )}
                </div>
                <div className="oficina-instalar-acoes">
                  {instalacao.podeInstalar ? (
                    <button type="button" className="oficina-botao oficina-botao-primario" onClick={instalacao.instalar}>Instalar</button>
                  ) : null}
                  <button type="button" className="oficina-icone-botao" aria-label="Dispensar" onClick={dispensarInstalacao}>
                    <i className="pi pi-times" />
                  </button>
                </div>
              </div>
            ) : null}
            {navegacao.tela === 'fotos' ? (
              <FotosInspecao
                key={navegacao.placa}
                placa={navegacao.placa}
                onNovaInspecao={novaInspecao}
                onSessaoExpirada={sair}
                setAviso={setAviso}
              />
            ) : (
              <PlacaVeiculo onContinuar={abrirFotos} setAviso={setAviso} />
            )}
          </main>
        </>
      ) : (
        <OficinaLogin />
      )}
      <Aviso aviso={aviso} onFechar={fecharAviso} />
    </div>
  );
}
